/**
 * Centralized discount configuration
 * This file controls discount amounts and eligible plans across the entire app
 */

// Discount amount in rials (500,000 toomans = 5,000,000 rials)
export const DISCOUNT_AMOUNT_RIALS = 4030000;

// Discount amount in toomans for display purposes
export const DISCOUNT_AMOUNT_TOOMANS = DISCOUNT_AMOUNT_RIALS / 10;

// Plan identifiers
export const PLAN_IDS = {
  O3_PRO: 'o3pro',
  PRIVATE: 'private',
  FAMILY: 'family',
  STUDENT: 'student',
  GEMINI: 'gemini',
  GEMINI_TRIAL: 'gemini-trial',
  GROK_FAMILY: 'grok-family',
  PRIVATE_GUARANTEE: 'private-guarantee',
  THREE_MONTH_NO_GUARANTEE: 'three-month-no-guarantee',
  ONE_MONTH_NO_GUARANTEE: 'one-month-no-guarantee'
};

// Original prices in rials
export const ORIGINAL_PRICES = {
  [PLAN_IDS.O3_PRO]: 27970000,
  [PLAN_IDS.PRIVATE]: 18970000,
  [PLAN_IDS.FAMILY]: 9970000,
  [PLAN_IDS.STUDENT]: 20000000,
  [PLAN_IDS.GEMINI]: 18970000,
  [PLAN_IDS.GEMINI_TRIAL]: 4500000,
  [PLAN_IDS.GROK_FAMILY]: 10000000,
  [PLAN_IDS.PRIVATE_GUARANTEE]: 25970000,
  [PLAN_IDS.THREE_MONTH_NO_GUARANTEE]: 29970000,
  [PLAN_IDS.ONE_MONTH_NO_GUARANTEE]: 15970000,
};

// --- DISCOUNT CONFIGURATION ---
// To disable discounts for a plan, simply remove it from this array.
export const DISCOUNT_ELIGIBLE_PLANS = [
  PLAN_IDS.O3_PRO,
  PLAN_IDS.PRIVATE,
  PLAN_IDS.PRIVATE_GUARANTEE,
  // PLAN_IDS.FAMILY,  // Currently disabled
  // PLAN_IDS.STUDENT, // Currently disabled
];

// Plans that include Grok bonus with discount
export const GROK_BONUS_PLANS = [
  PLAN_IDS.O3_PRO,
  PLAN_IDS.PRIVATE,
  PLAN_IDS.PRIVATE_GUARANTEE
];

/**
 * Check if a plan is eligible for any discount.
 * @param {string} planType - Plan type from PLAN_IDS
 * @returns {boolean}
 */
export const isEligibleForDiscount = (planType) => {
  return DISCOUNT_ELIGIBLE_PLANS.includes(planType);
};

/**
 * Calculate the discounted price for a plan.
 * Returns original price if the plan is not eligible or discount is not active.
 * @param {string} planType - Plan type from PLAN_IDS
 * @param {boolean} isDiscountActive - Whether the site-wide discount is active
 * @returns {number} - Price in rials
 */
export const getDiscountedPrice = (planType, isDiscountActive) => {
  const actualOriginalPrice = ORIGINAL_PRICES[planType];
  if (!actualOriginalPrice || !isDiscountActive || !isEligibleForDiscount(planType)) {
    return actualOriginalPrice;
  }
  
  // When discount is active, calculate from displayOriginalPrice (actual + 30000)
  // This ensures the discount is exactly 4030000 rials from the displayed original price
  const displayOriginalPrice = actualOriginalPrice + 30000;
  return displayOriginalPrice - DISCOUNT_AMOUNT_RIALS;
};

/**
 * Check if plan includes Grok bonus when discounted.
 * @param {string} planType - Plan type to check
 * @returns {boolean} - Whether plan includes Grok bonus
 */
export const includesGrokBonus = (planType) => {
  return GROK_BONUS_PLANS.includes(planType);
};

/**
 * Get all discount-related information for a plan.
 * @param {string} planType - Plan type from PLAN_IDS
 * @param {boolean} isDiscountActive - Whether the site-wide discount is active
 * @returns {object} - Complete discount information for the plan
 */
export const getDiscountInfo = (planType, isDiscountActive) => {
  const isEligible = isEligibleForDiscount(planType);
  const hasGrokBonus = includesGrokBonus(planType);
  const actualOriginalPrice = ORIGINAL_PRICES[planType];
  
  // When discount is active, show original price as 3000 more than actual
  // When discount ends, show the actual original price
  const displayOriginalPrice = (isDiscountActive && isEligible) 
    ? actualOriginalPrice + 30000 
    : actualOriginalPrice;

  return {
    originalPrice: actualOriginalPrice,
    displayOriginalPrice: displayOriginalPrice,
    discountedPrice: getDiscountedPrice(planType, isDiscountActive),
    discountAmount: DISCOUNT_AMOUNT_RIALS,
    discountAmountToomans: DISCOUNT_AMOUNT_TOOMANS,
    isEligible,
    isActive: isDiscountActive && isEligible,
    hasGrokBonus: hasGrokBonus && isDiscountActive && isEligible,
    savingsAmount: isDiscountActive && isEligible ? DISCOUNT_AMOUNT_RIALS : 0,
  };
}; 